import React, { useState, useEffect, useRef } from 'react';
import { sendChatbotMessage } from '../../api/aiServices';
import { getSessionUUID } from '../lib/AuthToken';
import { useAlert } from '../../providers/AlertProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import logger from '../../utils/logger';
import '../css/AIChatbot.css';

const AIChatbot = ({ theme = 'light', isOpen, onClose }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const { profile, isDev } = useAuthProvider();
    const { showAlert } = useAlert();

    const isLightTheme = theme === 'light';

    useEffect(() => {
        if (isOpen) {
            initializeChatbot();
        }
    }, [isOpen]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const initializeChatbot = async () => {
        // Add welcome message
        const welcomeMessage = {
            id: Date.now(),
            type: 'bot',
            content: {
                message: `Hello${profile?.first_name ? ` ${profile.first_name}` : ''}! 👋 I'm your customer support assistant. I can help you with YOUR order status, YOUR warehouse inventory, and support for YOUR account. What can I help you with today?`,
                type: 'info'
            },
            timestamp: new Date()
        };

        setMessages([welcomeMessage]);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const sendMessage = async (messageText = inputMessage.trim()) => {
        if (!messageText || loading) return;

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: { message: messageText },
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setLoading(true);

        try {
            const result = await sendChatbotMessage(messageText, messages);
            
            if (result.success) {
                const botMessage = {
                    id: Date.now() + 1,
                    type: 'bot',
                    content: result.response,
                    intent: result.intent,
                    interaction_id: result.interaction_id,
                    timestamp: new Date()
                };

                setMessages(prev => [...prev, botMessage]);
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            logger.error('Error sending message:', error);
            
            const errorMessage = {
                id: Date.now() + 1,
                type: 'bot',
                content: {
                    message: 'I apologize, but I encountered an error processing your message. Please try again or contact support if the issue persists.',
                    type: 'error'
                },
                timestamp: new Date()
            };

            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickAction = (action) => {
        const actionMessages = {
            'check_orders': 'What is the status of my orders?',
            'place_order': 'How do I place a new order?',
            'view_inventory': 'Can you show me my warehouse inventory?',
            'contact_support': 'How do I contact support?'
        };

        const message = actionMessages[action.id] || action.label;
        sendMessage(message);
    };





    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const getMessageTypeClass = (type) => {
        switch (type) {
            case 'success': return 'message-success';
            case 'error': return 'message-error';
            case 'info': return 'message-info';
            default: return '';
        }
    };

    const formatTime = (timestamp) => {
        return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (!isOpen) return null;

    return (
        <div className={`chatbot-overlay ${isLightTheme ? 'light' : 'dark'}`}>
            <div className={`chatbot-container ${isLightTheme ? 'light' : 'dark'}`}>
                {/* Header */}
                <div className="chatbot-header">
                    <div className="chatbot-title">
                        <span className="chatbot-icon">🤖</span>
                        <h3>Customer Support</h3>
                        <span className="customer-badge">CUSTOMER</span>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        ✕
                    </button>
                </div>

                {/* Messages */}
                <div className="chatbot-messages">
                    {messages.map((message) => (
                        <div key={message.id} className={`message ${message.type}-message`}>
                            <div className="message-content">
                                <div className={`message-bubble ${getMessageTypeClass(message.content.type)}`}>
                                    <p>{message.content.message}</p>
                                    
                                    {/* Order Details */}
                                    {message.content.order_details && (
                                        <div className="order-details">
                                            <strong>Order: {message.content.order_details.order_number}</strong>
                                            <br />
                                            <span>Status: {message.content.order_details.status}</span>
                                        </div>
                                    )}

                                    {/* Instructions */}
                                    {message.content.instructions && (
                                        <div className="instructions">
                                            {message.content.instructions.map((instruction, idx) => (
                                                <div key={idx} className="instruction-item">
                                                    {instruction}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Contact Info */}
                                    {message.content.contact_info && (
                                        <div className="contact-info">
                                            <strong>Business Hours:</strong> {message.content.contact_info.business_hours}
                                            <br />
                                            <strong>Emergency:</strong> {message.content.contact_info.emergency}
                                        </div>
                                    )}

                                    {/* Suggested Actions */}
                                    {message.content.suggested_actions && (
                                        <div className="suggested-actions">
                                            {message.content.suggested_actions.map((action, idx) => (
                                                <button
                                                    key={idx}
                                                    className="action-btn"
                                                    onClick={() => handleQuickAction({ id: action, label: action })}
                                                >
                                                    {action.replace('_', ' ').replace(/\\b\\w/g, l => l.toUpperCase())}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <span className="message-time">
                                    {formatTime(message.timestamp)}
                                </span>
                                

                            </div>
                        </div>
                    ))}
                    
                    {loading && (
                        <div className="message bot-message">
                            <div className="message-content">
                                <div className="message-bubble">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="chatbot-input">
                    <div className="input-container">
                        <textarea
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Type your message..."
                            disabled={loading}
                            rows="1"
                        />
                        <button
                            onClick={() => sendMessage()}
                            disabled={!inputMessage.trim() || loading}
                            className="send-btn"
                        >
                            ➤
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIChatbot;